/**
 * The interface language as a tiny observable, independent of every store so pure modules
 * (dispatch wording, the newspaper, the renderer) and unit tests can read it without pulling in
 * React or localStorage. `app/settings.ts` owns the persisted choice and pushes it here.
 */

export type Locale = 'en' | 'de';

/** Every supported locale, in the order the language switches list them. */
export const LOCALES: readonly Locale[] = ['en', 'de'];

/** Name of each language in itself (switch labels). */
export const LOCALE_NAME: Record<Locale, string> = { en: 'English', de: 'Deutsch' };

let current: Locale = 'en';
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

export function setLocale(locale: Locale): void {
  if (locale === current) return;
  current = locale;
  for (const fn of listeners) fn();
}

/** Subscribe to locale changes; returns the unsubscribe function (useSyncExternalStore shape). */
export function onLocale(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** First-run default from the browser: any German variant (de, de-AT, de-CH …) → German. */
export function detectLocale(language: string | undefined): Locale {
  return language?.toLowerCase().startsWith('de') ? 'de' : 'en';
}

/** A value worded once per locale (teletype lines, telegrams, replay markers): switch-proof. */
export type Localized<T> = Record<Locale, T>;

/** Build a value in every locale at once, so a later language switch never re-words history. */
export function inEveryLocale<T>(make: (locale: Locale) => T): Localized<T> {
  return Object.fromEntries(LOCALES.map((l) => [l, make(l)])) as Localized<T>;
}
